export const guestAnalyticsStateStorageKey =
  "pantryverse.productAnalytics.guestIdentity.v1";

export interface GuestAnalyticsStateStorage {
  read(): string | null;
  write(value: string): void;
  clear(): void;
}

interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const createPageMemoryGuestAnalyticsStateStorage =
  (): GuestAnalyticsStateStorage => {
    let value: string | null = null;
    return {
      read: () => value,
      write: (next) => {
        value = next;
      },
      clear: () => {
        value = null;
      },
    };
  };

/**
 * Keeps first-party browser storage behind a failure-contained adapter. Once
 * durable storage fails, this instance remains page-memory-only so a record
 * that could not be cleared cannot reappear later in the same page lifecycle.
 */
export const createBrowserGuestAnalyticsStateStorage = (
  loadStorage: () => BrowserStorage,
): GuestAnalyticsStateStorage => {
  const memory = createPageMemoryGuestAnalyticsStateStorage();
  let durable: BrowserStorage | undefined;
  let durableAvailable = true;

  try {
    durable = loadStorage();
  } catch {
    durableAvailable = false;
  }

  const disableDurableStorage = (): void => {
    durableAvailable = false;
    durable = undefined;
  };

  return {
    read: () => {
      if (!durableAvailable || !durable) return memory.read();
      try {
        const value = durable.getItem(guestAnalyticsStateStorageKey);
        if (value === null) {
          memory.clear();
        } else {
          memory.write(value);
        }
        return value;
      } catch {
        disableDurableStorage();
        return memory.read();
      }
    },
    write: (value) => {
      memory.write(value);
      if (!durableAvailable || !durable) return;
      try {
        durable.setItem(guestAnalyticsStateStorageKey, value);
      } catch {
        disableDurableStorage();
      }
    },
    clear: () => {
      memory.clear();
      if (!durableAvailable || !durable) return;
      try {
        durable.removeItem(guestAnalyticsStateStorageKey);
      } catch {
        disableDurableStorage();
      }
    },
  };
};
